import { useCallback, useRef } from "react";
import { DismissSwipeArgs } from "./DismissSwipeArgs";
import { SwipeDirection } from "./SwipeDirection";
import {
  DISMISS_SWIPE_DEFAULT_ARGS,
  DISMISS_SWIPE_VAR_DRAG_LEFT,
  DISMISS_SWIPE_VAR_DRAG_RIGHT
} from "./dismissSwipe.constant";

/**
 * Returns a callback-ref that attaches horizontal swipe-to-dismiss
 * pointer listeners to the referenced element.
 *
 * Designed for singleton-rendered components like Brioche where the
 * same DOM node is reused across multiple React re-renders:
 * - The ref callback has a **stable identity** (no changing deps),
 *   so listeners are registered exactly once per mount.
 * - Cleanup runs automatically when the ref is called with `null`
 *   (unmount) or when a new element replaces the old one.
 */
export const useDismissSwipe = (
  args?: Partial<DismissSwipeArgs>
): ((element: HTMLDivElement | null) => void) => {
  const merged = { ...DISMISS_SWIPE_DEFAULT_ARGS, ...args };

  // Latest-value refs keep the stable callback ref up-to-date
  // without forcing a new function identity on every render.
  const onDismissRef = useRef(merged.onDismiss);
  const thresholdRef = useRef(merged.threshold);
  onDismissRef.current = merged.onDismiss;
  thresholdRef.current = merged.threshold;

  const cleanupRef = useRef<(() => void) | null>(null);

  const thresholdReached = (
    dx: number = 0
  ): { reached: boolean; direction: SwipeDirection } => {
    const thresholdPx = thresholdRef.current;
    const direction: SwipeDirection = dx > 0 ? "right" : "left";

    return { reached: Math.abs(dx) >= thresholdPx, direction };
  };

  return useCallback((element: HTMLDivElement | null) => {
    // Tear down previous listeners (if any)
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    if (!element) {
      return;
    }
    element.style.setProperty(DISMISS_SWIPE_VAR_DRAG_LEFT, "0px");
    element.style.setProperty(DISMISS_SWIPE_VAR_DRAG_RIGHT, "0px");

    let startX = 0;
    let isButtonDown = false;
    let isDragging = false;

    const onPointerDown = (e: PointerEvent) => {
      if (
        e.button !== 0 ||
        (e.target as HTMLElement | undefined)?.closest("button, a")
      ) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      startX = e.clientX;
      isButtonDown = true;
      element.setPointerCapture(e.pointerId);
    };

    const endDragging = () => {
      isDragging = false;
      isButtonDown = false;
      element.style.setProperty(DISMISS_SWIPE_VAR_DRAG_LEFT, "0px");
      element.style.setProperty(DISMISS_SWIPE_VAR_DRAG_RIGHT, "0px");
    };

    const onPointerMove = (e: PointerEvent) => {
      console.log(e.buttons);
      if (!isButtonDown) {
        return;
      }
      isDragging = true;
      e.preventDefault();
      e.stopPropagation();

      const dx = e.clientX - startX;
      const { reached, direction } = thresholdReached(dx);
      if (reached) {
        endDragging();
        onDismissRef.current(direction);
        return;
      }
      switch (direction) {
        case "left":
          element.style.setProperty(
            DISMISS_SWIPE_VAR_DRAG_LEFT,
            `${Math.abs(dx)}px`
          );
          element.style.setProperty(DISMISS_SWIPE_VAR_DRAG_RIGHT, "0px");
          break;
        case "right":
          element.style.setProperty(DISMISS_SWIPE_VAR_DRAG_LEFT, "0px");
          element.style.setProperty(
            DISMISS_SWIPE_VAR_DRAG_RIGHT,
            `${Math.abs(dx)}px`
          );
          break;
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!isButtonDown) {
        return;
      }
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
      endDragging();
    };

    // Prevent the browser from claiming horizontal panning
    // while still allowing vertical scrolling.
    element.style.touchAction = "pan-y";

    element.addEventListener("pointerdown", onPointerDown);
    element.addEventListener("pointermove", onPointerMove);
    element.addEventListener("pointerup", onPointerUp);
    element.addEventListener("pointercancel", onPointerUp);

    cleanupRef.current = () => {
      element.removeEventListener("pointerdown", onPointerDown);
      element.removeEventListener("pointermove", onPointerMove);
      element.removeEventListener("pointerup", onPointerUp);
      element.removeEventListener("pointercancel", onPointerUp);
      element.style.touchAction = "";
    };
  }, []);
};
